import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Briefcase, Search, ChevronDown, ChevronUp, User, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import { recruiterJobOfferService } from '@/services/jobOfferService'
import { useQueryClient } from '@tanstack/react-query'
import { jobProspectsKeys } from '@/hooks/useJobProspects'
import recruiterJobService from '@/services/recruiterJobService'
import jobProspectService from '@/services/jobProspectService'
import { toast } from '@/stores/toastStore'
import { CreateJobOfferRequest, EmploymentType, WorkMode, RecruiterJob, JobProspect } from '@/types'

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERNSHIP', label: 'Internship' },
]

const workModeOptions = [
  { value: 'REMOTE', label: 'Remote' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'ONSITE', label: 'Onsite' },
]

const currencyOptions = [
  { value: 'TND', label: 'TND (د.ت)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
]

export default function SendJobOfferPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('jobOffers')

  const preselectedCandidateId = searchParams.get('candidateId') || ''
  const preselectedCandidateName = searchParams.get('candidateName') || 'Candidate'
  const preselectedJobId = searchParams.get('jobId') || ''

  const [step, setStep] = useState<'select-prospect' | 'fill-offer'>(
    preselectedCandidateId ? 'fill-offer' : 'select-prospect'
  )
  const [, setCandidateId] = useState(preselectedCandidateId)
  const [candidateName, setCandidateName] = useState(preselectedCandidateName)

  const [submitting, setSubmitting] = useState(false)
  const [jobs, setJobs] = useState<RecruiterJob[]>([])
  const [selectedJobId, setSelectedJobId] = useState(preselectedJobId)
  const [formData, setFormData] = useState<Partial<CreateJobOfferRequest>>({
    candidateId: preselectedCandidateId,
    recruiterJobId: preselectedJobId || undefined,
    position: '',
    location: '',
    employmentType: 'FULL_TIME',
    workMode: 'HYBRID',
    salaryMin: undefined,
    salaryMax: undefined,
    salaryCurrency: 'TND',
    benefits: '',
    message: '',
    description: '',
    expiresAt: '',
  })

  // Prospect search state
  const [prospects, setProspects] = useState<JobProspect[]>([])
  const [prospectsLoading, setProspectsLoading] = useState(false)
  const [prospectSearch, setProspectSearch] = useState('')
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null)

  useEffect(() => {
    recruiterJobService
      .listJobs({ limit: 100 })
      .then((r) => {
        setJobs(r.jobs)
        if (preselectedJobId && r.jobs.some((j) => j.id === preselectedJobId)) {
          setSelectedJobId(preselectedJobId)
          applyJob(preselectedJobId, r.jobs)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (step === 'select-prospect') {
      loadProspects()
    }
  }, [step])

  const loadProspects = async () => {
    try {
      setProspectsLoading(true)
      const allProspects: JobProspect[] = []
      for (const job of jobs.length > 0
        ? jobs
        : (await recruiterJobService.listJobs({ limit: 100 })).jobs) {
        try {
          const res = await jobProspectService.listProspects({
            recruiterJobId: job.id,
            limit: 50,
          })
          allProspects.push(...res.prospects)
        } catch {
          // skip jobs that fail
        }
      }
      setProspects(allProspects)
    } catch {
      toast.error('Failed to load prospects')
    } finally {
      setProspectsLoading(false)
    }
  }

  const applyJob = (jobId: string, jobsList?: RecruiterJob[]) => {
    const list = jobsList || jobs
    const job = list.find((j) => j.id === jobId)
    if (!job) return
    let expiresAt = ''
    if (job.defaultExpiryDays) {
      const d = new Date()
      d.setDate(d.getDate() + job.defaultExpiryDays)
      expiresAt = d.toISOString().split('T')[0]
    }
    setFormData((prev) => ({
      ...prev,
      recruiterJobId: job.id,
      position: job.position || '',
      location: job.location || '',
      employmentType: job.employmentType || 'FULL_TIME',
      workMode: job.workMode || 'HYBRID',
      salaryMin: job.salaryMin ?? undefined,
      salaryMax: job.salaryMax ?? undefined,
      salaryCurrency: job.salaryCurrency || 'TND',
      benefits: job.benefits || '',
      description: job.description || '',
      expiresAt,
    }))
  }

  const handleInputChange = (
    field: keyof CreateJobOfferRequest,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSelectProspect = (prospect: JobProspect) => {
    const name =
      prospect.candidate?.firstName || prospect.candidate?.lastName
        ? `${prospect.candidate?.firstName ?? ''} ${prospect.candidate?.lastName ?? ''}`.trim()
        : 'Candidate'
    setCandidateId(prospect.candidateId)
    setCandidateName(name)
    setFormData((prev) => ({
      ...prev,
      candidateId: prospect.candidateId,
    }))
    if (prospect.recruiterJobId && !selectedJobId) {
      setSelectedJobId(prospect.recruiterJobId)
      applyJob(prospect.recruiterJobId)
    }
    setStep('fill-offer')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !selectedJobId ||
      !formData.position ||
      !formData.message ||
      !formData.employmentType ||
      !formData.workMode
    ) {
      toast.error('Please fill in all required fields')
      return
    }
    try {
      setSubmitting(true)
      const submitData = { ...formData, recruiterJobId: selectedJobId } as CreateJobOfferRequest
      if (submitData.expiresAt && submitData.expiresAt !== '') {
        const date = new Date(submitData.expiresAt)
        date.setHours(23, 59, 59, 999)
        submitData.expiresAt = date.toISOString()
      } else {
        submitData.expiresAt = undefined
      }
      await recruiterJobOfferService.createJobOffer(submitData)
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      toast.success(`Job offer sent to ${candidateName}!`)
      navigate('/recruiter/job-offers')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send job offer')
    } finally {
      setSubmitting(false)
    }
  }

  // Group prospects by job for the selection UI
  const prospectsByJob = useMemo(() => {
    const q = prospectSearch.toLowerCase()
    const filtered = prospects.filter((p) => {
      if (!q) return true
      const name = `${p.candidate?.firstName ?? ''} ${p.candidate?.lastName ?? ''}`.toLowerCase()
      return name.includes(q)
    })
    const grouped: Record<string, JobProspect[]> = {}
    for (const p of filtered) {
      const key = p.recruiterJobId || 'ungrouped'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(p)
    }
    return grouped
  }, [prospects, prospectSearch])

  // Prospect selection step
  if (step === 'select-prospect') {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Send Job Offer</h1>
        <p className="text-gray-600 mb-6">Select a prospect to send an offer to</p>

        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search prospects by name..."
            value={prospectSearch}
            onChange={(e) => setProspectSearch(e.target.value)}
            icon={<Search className="w-5 h-5" />}
          />
        </div>

        {prospectsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : Object.keys(prospectsByJob).length === 0 ? (
          <div className="text-center py-16">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Prospects Found</h2>
            <p className="text-gray-600 mb-6">
              Add candidates as prospects from Search or AI Matching first.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(prospectsByJob).map(([jobId, jobProspects]) => {
              const job = jobs.find((j) => j.id === jobId)
              const isExpanded = expandedJobId === jobId
              return (
                <div
                  key={jobId}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedJobId(isExpanded ? null : jobId)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-900">
                        {job?.title || 'Unknown Job'}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({jobProspects.length} prospects)
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                  {(isExpanded || Object.keys(prospectsByJob).length <= 3) && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {jobProspects.map((prospect) => {
                        const name =
                          prospect.candidate?.firstName || prospect.candidate?.lastName
                            ? `${prospect.candidate?.firstName ?? ''} ${prospect.candidate?.lastName ?? ''}`.trim()
                            : 'Candidate'
                        return (
                          <button
                            key={prospect.id}
                            onClick={() => handleSelectProspect(prospect)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                          >
                            {prospect.candidate?.profilePhotoUrl ? (
                              <img
                                src={prospect.candidate.profilePhotoUrl}
                                alt=""
                                className="w-8 h-8 rounded-full rounded-avatar object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full rounded-avatar bg-gray-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{name}</p>
                              {prospect.candidate?.location && (
                                <p className="text-xs text-gray-500">
                                  {prospect.candidate.location}
                                </p>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="secondary"
          onClick={() => {
            if (!preselectedCandidateId) {
              setStep('select-prospect')
            } else {
              navigate(-1)
            }
          }}
          className="mb-4 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Send Job Offer</h1>
        <p className="text-gray-600 mt-1">
          Sending to <span className="font-semibold text-gray-800">{candidateName}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Job selection — required */}
        {jobs.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <Select
              label={t('jobs.selector.label', 'Select a job *')}
              value={selectedJobId}
              onChange={(e) => {
                setSelectedJobId(e.target.value)
                applyJob(e.target.value)
              }}
              options={[
                { value: '', label: 'Choose a job…' },
                ...jobs.map((job) => ({
                  value: job.id,
                  label: job.title + (job.position ? ` — ${job.position}` : ''),
                })),
              ]}
            />
            {selectedJobId && (
              <p className="text-sm text-blue-600 mt-2">
                ✓ {t('jobs.selector.applied', 'Job fields applied')}
              </p>
            )}
          </div>
        )}

        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 space-y-5">
          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.position || ''}
              onChange={(e) => handleInputChange('position', e.target.value)}
              placeholder="e.g., Senior Software Engineer"
              icon={<Briefcase className="w-5 h-5" />}
              required
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <Input
              type="text"
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="e.g., Tunis, Tunisia"
            />
          </div>

          {/* Employment Type & Work Mode */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label={
                <>
                  Employment Type <span className="text-red-500">*</span>
                </>
              }
              value={formData.employmentType}
              onChange={(e) =>
                handleInputChange('employmentType', e.target.value as EmploymentType)
              }
              options={employmentTypeOptions}
              required
            />
            <Select
              label={
                <>
                  Work Mode <span className="text-red-500">*</span>
                </>
              }
              value={formData.workMode}
              onChange={(e) => handleInputChange('workMode', e.target.value as WorkMode)}
              options={workModeOptions}
              required
            />
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Salary</label>
              <Input
                type="number"
                value={formData.salaryMin || ''}
                onChange={(e) =>
                  handleInputChange(
                    'salaryMin',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                placeholder="e.g., 30000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Salary</label>
              <Input
                type="number"
                value={formData.salaryMax || ''}
                onChange={(e) =>
                  handleInputChange(
                    'salaryMax',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                placeholder="e.g., 50000"
              />
            </div>
            <Select
              label="Currency"
              value={formData.salaryCurrency}
              onChange={(e) => handleInputChange('salaryCurrency', e.target.value)}
              options={currencyOptions}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={formData.message || ''}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Personalized message to the candidate..."
              rows={4}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Job description and details..."
              rows={4}
            />
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
            <Textarea
              value={formData.benefits || ''}
              onChange={(e) => handleInputChange('benefits', e.target.value)}
              placeholder="Benefits and perks..."
              rows={3}
            />
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offer Expires On</label>
            <Input
              type="date"
              value={formData.expiresAt || ''}
              onChange={(e) => handleInputChange('expiresAt', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={submitting || !selectedJobId}
            className="flex-1"
          >
            Send Job Offer
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}
